import { BackendRoute, Language, languagesToAbbreviationMap, StorageKey } from 'consts';

import { HttpException } from './http.exception';

export class ApiService {
  constructor(private readonly backendUrl = process.env.REACT_APP_BACKEND_URL) {}

  private buildQueryParam(url: string, queryParams?: Record<string, unknown>) {
    if (!queryParams) return url;
    const queries: string[] = [];
    for (const [key, value] of Object.entries(queryParams)) {
      if (value === undefined || value === '') continue;
      if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
        queries.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        continue;
      }
      queries.push(`${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`);
    }
    return `${url}${queries.length > 0 ? '?' + queries.join('&') : ''}`;
  }

  private buildRoutePath(routePath: (string | number)[] = []) {
    return routePath.length > 0 ? '/' + routePath.join('/') : '';
  }

  public async get<T>(
    {
      endpoint,
      routePath = [],
    }: {
      endpoint: BackendRoute;
      routePath?: (string | number)[];
    },
    params: { queryParams?: Record<string, unknown> } = {},
  ) {
    return this.request<T>(endpoint + this.buildRoutePath(routePath), {
      ...params,
    });
  }

  public async post<T, B = unknown>(
    { endpoint, routePath = [] }: { endpoint: BackendRoute; routePath?: (string | number)[] },
    { body, ...params }: { queryParams?: Record<string, unknown>; body?: B } = {},
  ) {
    return this.request<T>(endpoint + this.buildRoutePath(routePath), {
      init: {
        method: 'POST',
        body: JSON.stringify(body),
      },
      ...params,
    });
  }

  private async basicRequest<T>(url: string, init?: RequestInit | undefined) {
    let res: Response;
    try {
      res = await fetch(url, init);
    } catch (e) {
      console.error(e);
      throw e;
    }
    const body = (
      res.headers.get('Content-Type')?.includes('application/json')
        ? await res.json()
        : await res.text()
    ) as T;
    if (!res.ok) throw new HttpException(res, body as { message?: string });
    return body;
  }

  private async request<T>(
    route: string,
    {
      init,
      ...params
    }: {
      init?: RequestInit | undefined;
      queryParams?: Record<string, unknown>;
    } = {},
  ): Promise<T> {
    const url = `${this.backendUrl}${route}`;
    const res = await this.basicRequest<T>(this.buildQueryParam(url, params.queryParams), {
      credentials: 'include',
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...init?.headers,
        'Accept-Language': this.getLanguage(),
      },
    });
    return res;
  }

  public getLanguage() {
    const language = localStorage.getItem(StorageKey.LANGUAGE);
    return languagesToAbbreviationMap[(language || Language.ENGLISH) as Language];
  }
}

const apiService = new ApiService();
export default apiService;
